import { Pencil, UserPlus } from "lucide-react";
import { useCanAccess, useGetList, useNotify, useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";
import { AccessDenied } from "@/components/admin/access-denied";
import { useIsMobile } from "@/hooks/use-mobile";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { CrmDataProvider } from "../providers/types";
import { SettingsTabs } from "../settings/SettingsTabs";
import type { Project, Sale, SalesProject } from "../types";
import { AccessSelect, type AccessChoice } from "./AccessSelect";
import { byPosition } from "./byPosition";
import { useProjectAdmin } from "./useProjectAdmin";

const LIST = { pagination: { page: 1, perPage: 1000 } };

/**
 * Настройки → Сотрудники (owner only). Owner 05.11: «Будем давать уровни
 * доступа индивидуально». For each employee: what they see in each project
 * (nothing / own / department / all) and the personal rights to delete and
 * to export. Roles are only presets. Also the extension (sales.phone_ext):
 * who talked on a call, and «Позвонить» in the cards.
 */
export const StaffAccess = () => {
  const { canAccess, isPending } = useCanAccess({
    resource: "sales",
    action: "edit",
  });
  const isMobile = useIsMobile();
  if (isPending) return null;
  if (!canAccess) return <AccessDenied />;
  return (
    <div className="mt-4 pb-20 flex flex-col gap-6">
      {isMobile ? null : <SettingsTabs />}
      <StaffList />
    </div>
  );
};

StaffAccess.path = "/settings/staff";

const StaffList = () => {
  const translate = useTranslate();
  const { data: sales } = useGetList<Sale>("sales", {
    ...LIST,
    sort: { field: "last_name", order: "ASC" },
  });
  const { data: projectData } = useGetList<Project>("projects", {
    ...LIST,
    sort: { field: "position", order: "ASC" },
  });
  const { data: rows } = useGetList<SalesProject>("sales_projects", LIST);
  const projects = useMemo(
    () => [...(projectData ?? [])].filter((p) => !p.archived).sort(byPosition),
    [projectData],
  );
  const active = (sales ?? []).filter((s) => !s.disabled);

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <div className="flex items-center justify-between gap-4">
        <h2 className="vv-heading">{translate("crm.projects.staff_title")}</h2>
        <Link
          to="/sales/create"
          className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
        >
          <UserPlus className="size-4" />
          {translate("crm.projects.new_employee")}
        </Link>
      </div>
      <p className="text-sm text-muted-foreground">
        {translate("crm.calls.ext_hint")}
      </p>
      {active.map((sale) => (
        <EmployeeCard
          key={sale.id}
          sale={sale}
          projects={projects}
          rows={(rows ?? []).filter(
            (r) => String(r.sales_id) === String(sale.id),
          )}
        />
      ))}
    </div>
  );
};

const EmployeeCard = ({
  sale,
  projects,
  rows,
}: {
  sale: Sale;
  projects: Project[];
  rows: SalesProject[];
}) => {
  const translate = useTranslate();
  const run = useProjectAdmin();
  const isOwner = sale.role === "owner" || sale.administrator;
  const fullName = `${sale.first_name} ${sale.last_name}`;

  const setAccess = (project: Project, value: AccessChoice) => {
    const row = rows.find((r) => String(r.project_id) === String(project.id));
    run(async (dataProvider) => {
      if (value === "none") {
        if (row)
          await dataProvider.delete("sales_projects", {
            id: row.id,
            previousData: row,
          });
      } else if (row) {
        await dataProvider.update<SalesProject>("sales_projects", {
          id: row.id,
          data: { access: value },
          previousData: row,
        });
      } else {
        await dataProvider.create<SalesProject>("sales_projects", {
          data: { sales_id: sale.id, project_id: project.id, access: value },
        });
      }
    });
  };

  const setRight = (right: "can_delete" | "can_export", value: boolean) =>
    run((dataProvider) =>
      (dataProvider as CrmDataProvider).salesUpdate(sale.id, {
        [right]: value,
      } as Parameters<CrmDataProvider["salesUpdate"]>[1]),
    );

  return (
    <Card data-employee={fullName}>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{fullName}</p>
            <p className="text-xs text-muted-foreground">
              {translate(
                `crm.projects.roles.${isOwner ? "owner" : (sale.role ?? "manager")}`,
              )}
              {sale.department ? ` · ${sale.department}` : ""}
            </p>
          </div>
          <Link
            to={`/sales/${sale.id}`}
            aria-label={`${translate("crm.projects.edit_employee")} ${fullName}`}
            className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
          >
            <Pencil className="size-4" />
            {translate("crm.projects.edit_employee")}
          </Link>
        </div>
        <PhoneExtInput sale={sale} />
        {isOwner ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.projects.owner_sees_all")}
          </p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="sr-only">
                <tr>
                  <th>{translate("crm.projects.switcher")}</th>
                  <th>{translate("crm.projects.access.label")}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {projects.map((project) => {
                  const row = rows.find(
                    (r) => String(r.project_id) === String(project.id),
                  );
                  return (
                    <tr key={project.id}>
                      <td className="py-1.5 pr-3">
                        <span className="flex items-center gap-2">
                          <span
                            className="size-2 shrink-0 rounded-full bg-muted-foreground"
                            style={
                              project.color
                                ? { background: project.color }
                                : undefined
                            }
                            aria-hidden
                          />
                          {project.name}
                        </span>
                      </td>
                      <td className="py-1.5 text-right">
                        <AccessSelect
                          label={`${fullName}: ${project.name}`}
                          value={row?.access ?? "none"}
                          onChange={(value) => setAccess(project, value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {(["can_delete", "can_export"] as const).map((right) => (
                <label key={right} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sale[right] === true}
                    onChange={(event) => setRight(right, event.target.checked)}
                    className="size-4 accent-primary"
                  />
                  {translate(`crm.projects.${right}`)}
                </label>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/** «Добавочный»: saved when the field is left or Enter is pressed. */
const PhoneExtInput = ({ sale }: { sale: Sale }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const run = useProjectAdmin();
  const fullName = `${sale.first_name} ${sale.last_name}`;
  const current = sale.phone_ext ?? "";
  const id = `phone-ext-${sale.id}`;

  const save = async (input: HTMLInputElement) => {
    const value = input.value.trim();
    if (value === current) return;
    const done = await run((dataProvider) =>
      (dataProvider as CrmDataProvider).salesUpdate(sale.id, {
        phone_ext: value || null,
      } as Parameters<CrmDataProvider["salesUpdate"]>[1]),
    );
    if (done) notify("crm.calls.ext_saved", { type: "success" });
    else input.value = current;
  };

  return (
    <div className="flex items-center gap-3 text-sm">
      <label htmlFor={id} className="text-muted-foreground shrink-0">
        {translate("crm.calls.ext_label")}
      </label>
      <Input
        // a new value from the server resets the field
        key={current}
        id={id}
        defaultValue={current}
        aria-label={`${fullName}: ${translate("crm.calls.ext_label")}`}
        maxLength={64}
        autoComplete="off"
        className="h-8 w-40 min-w-0"
        onBlur={(event) => void save(event.currentTarget)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
      />
    </div>
  );
};
